import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import MinistryIcon from "../components/MinistryIcon";
import Modal from "../components/Modal";
import Spinner from "../components/Spinner";
import { useVolunteers } from "../lib/useVolunteers";
import type { Volunteer } from "../types/volunteer";
import { dedupeRoles } from "../lib/role-label";

// Roster browser — every volunteer Colleen has, searchable by name,
// click any row to open the existing /volunteer/:id history modal.
// Reachable from the Volunteers button on the dashboard.
export default function VolunteersModal() {
  const location = useLocation();
  const navigate = useNavigate();
  const { data, error, loading } = useVolunteers();
  const [filter, setFilter] = useState("");

  const close = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/", { replace: true });
  };

  const sorted = useMemo(() => sortByLastName(data), [data]);
  const visible = useMemo(() => applyFilter(sorted, filter), [sorted, filter]);

  return (
    <Modal open onClose={close} ariaLabel="Volunteers">
      <header className="volunteers-roster__header">
        <h2 className="volunteers-roster__title">Volunteers</h2>
        <p className="volunteers-roster__lede">
          Click a name for their history calendar — check-ins,
          assignments, and sub-request activity over the last 6 months.
        </p>
      </header>

      <label className="volunteers-roster__search">
        <span className="visually-hidden">Search by name</span>
        <input
          type="search"
          placeholder="Search by name…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          autoFocus
        />
      </label>

      {loading ? (
        <Spinner label="Loading volunteers" />
      ) : error ? (
        <p className="placeholder">Couldn't load: {error}</p>
      ) : visible.length === 0 ? (
        <p className="panel__empty">
          {sorted.length === 0
            ? "No volunteers on the roster yet."
            : `No matches for "${filter}".`}
        </p>
      ) : (
        <ul className="volunteers-roster__list">
          {visible.map((v) => (
            <li key={v.id} className="volunteers-roster__row">
              <Link
                to={`/volunteer/${v.id}`}
                state={{ backgroundLocation: location }}
                className="volunteers-roster__link"
              >
                <span className="volunteers-roster__name">
                  {v.firstName} {v.lastName}
                </span>
                <span className="volunteers-roster__ministries" aria-hidden="true">
                  {dedupeRoles(v.ministries).map((role) => (
                    <span key={role} className="volunteers-roster__ministry" title={role}>
                      <MinistryIcon ministry={role} />
                    </span>
                  ))}
                </span>
                <span className="volunteers-roster__chevron" aria-hidden="true">
                  ›
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

function sortByLastName(list: Volunteer[]): Volunteer[] {
  return [...list].sort((a, b) => {
    const al = a.lastName.toLowerCase();
    const bl = b.lastName.toLowerCase();
    if (al !== bl) return al < bl ? -1 : 1;
    return a.firstName.toLowerCase() < b.firstName.toLowerCase() ? -1 : 1;
  });
}

function applyFilter(list: Volunteer[], filter: string): Volunteer[] {
  const q = filter.trim().toLowerCase();
  if (!q) return list;
  return list.filter((v) => {
    const full = `${v.firstName} ${v.lastName}`.toLowerCase();
    return full.includes(q) || v.lastName.toLowerCase().includes(q);
  });
}
